import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography, Button, Modal, TextField, IconButton, Grid, CircularProgress, Alert } from '@mui/material';

export default function EmailTemplateFormModal({ open, onClose, onSave, editingTemplate = null, loading = false }) {
    const [formData, setFormData] = useState({ subject: '', body: '' });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open && editingTemplate) {
            setFormData({
                subject: editingTemplate.subject || '',
                body: editingTemplate.body || ''
            });
            setErrors({});
        }
    }, [open, editingTemplate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
        if (!formData.body.trim()) newErrors.body = 'Template body is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSave(editingTemplate ? { ...editingTemplate, ...formData } : formData);
        }
    };

    return (
        <Modal open={open} onClose={loading ? null : onClose} disableEscapeKeyDown>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: 600 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
                display: 'flex', flexDirection: 'column', gap: 2.5, maxHeight: '90vh', overflowY: 'auto'
            }}>
                <IconButton 
                    sx={{ position: 'absolute', top: 12, right: 12 }} 
                    onClick={onClose}
                    disabled={loading}
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold">
                    Edit Email Template: {editingTemplate?.name}
                </Typography>

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    You can use variables like <code>{`{{guest_name}}`}</code>, <code>{`{{booking_id}}`}</code>, and <code>{`{{check_in_date}}`}</code> in your template.
                </Alert>

                <Grid container spacing={2}>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email Subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            error={!!errors.subject}
                            helperText={errors.subject}
                            disabled={loading}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={10}
                            label="Email Body"
                            name="body"
                            value={formData.body}
                            onChange={handleChange}
                            error={!!errors.body}
                            helperText={errors.body}
                            disabled={loading}
                            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace' } }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Button 
                        fullWidth 
                        variant="outlined" 
                        color="error" 
                        onClick={onClose} 
                        size="large"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleSubmit} 
                        size="large"
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={20} color="inherit" />}
                    >
                        {loading ? 'Saving...' : 'Update Template'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
